import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import WorkspacePage from '../components/WorkspacePage';

const emptyPeriodForm = { name: '', code: '', type: 'academic_year', startDate: '', endDate: '', track: '', description: '' };

export default function AcademicPeriods() {
  const [periods, setPeriods] = useState([]);
  const [students, setStudents] = useState([]);
  const [selected, setSelected] = useState('');
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [studentSearch, setStudentSearch] = useState('');
  const [studentGradeFilter, setStudentGradeFilter] = useState('');
  const [grade, setGrade] = useState('');
  const [error, setError] = useState('');
  const [form, setForm] = useState(emptyPeriodForm);
  const [cohortPeriodId, setCohortPeriodId] = useState('');
  const [cohortStudentIds, setCohortStudentIds] = useState([]);
  const [cohortSearch, setCohortSearch] = useState('');
  const [cohortMessage, setCohortMessage] = useState('');
  const [cohortError, setCohortError] = useState('');
  const [cohortBusy, setCohortBusy] = useState(false);

  const load = () => {
    api.get('/academic-periods').then(({ data }) => setPeriods(data)).catch(() => setError('Could not load academic periods.'));
    api.get('/students').then(({ data }) => setStudents(data)).catch(() => {});
  };
  useEffect(load, []);

  const save = async (event) => {
    event.preventDefault();
    try {
      const { data } = await api.post('/academic-periods', form);
      setPeriods((current) => [data, ...current]);
      setForm(emptyPeriodForm);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not create period.');
    }
  };

  const initialize = async (id) => {
    try {
      const { data } = await api.post(`/academic-periods/${id}/initialize`);
      alert(`${data.created} student enrollment records created.`);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not initialize period.');
    }
  };

  const promote = async () => {
    if (!selected || !selectedStudents.length || !grade) return setError('Choose a target period, students, and new grade.');
    try {
      await api.post('/academic-periods/promote', {
        targetPeriodId: selected,
        studentIds: selectedStudents,
        newGrade: grade,
        status: 'promoted',
      });
      setSelectedStudents([]);
      setGrade('');
      setError('Progression recorded successfully.');
      load();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not record progression.');
    }
  };

  const eligibleCohorts = periods.filter((period) => period.type === 'cohort' && !['completed', 'archived'].includes(period.status));
  const activeStudents = students.filter((student) => student.status === 'active');
  const visibleCohortStudents = activeStudents.filter((student) => {
    const text = [student.user?.name, student.user?.email, student.admissionNumber, student.grade, student.section]
      .filter(Boolean).join(' ').toLowerCase();
    return text.includes(cohortSearch.toLowerCase());
  });

  const toggleVisibleCohortStudents = () => {
    const visibleIds = visibleCohortStudents.map((student) => student._id);
    setCohortStudentIds((current) => visibleIds.length && visibleIds.every((id) => current.includes(id))
      ? current.filter((id) => !visibleIds.includes(id))
      : [...new Set([...current, ...visibleIds])]);
  };

  const enrollSelectedInCohort = async () => {
    if (!cohortPeriodId || !cohortStudentIds.length) {
      setCohortError('Choose a cohort and at least one student.');
      return;
    }
    setCohortBusy(true);
    setCohortError('');
    setCohortMessage('');
    try {
      const { data } = await api.post(`/academic-periods/${cohortPeriodId}/enroll-students`, { studentIds: cohortStudentIds });
      setCohortMessage(data.message || 'Cohort enrollment completed.');
      setCohortStudentIds([]);
    } catch (err) {
      setCohortError(err.response?.data?.message || 'Could not enroll the selected students.');
    } finally {
      setCohortBusy(false);
    }
  };

  const visibleStudents = students.filter((student) => {
    const text = [student.user?.name, student.user?.email, student.admissionNumber, student.grade, student.section]
      .filter(Boolean).join(' ').toLowerCase();
    return text.includes(studentSearch.toLowerCase()) && (!studentGradeFilter || student.grade === studentGradeFilter);
  });
  const grades = [...new Set(students.map((student) => student.grade).filter(Boolean))].sort();
  const toggleVisible = () => {
    const ids = visibleStudents.map((student) => student._id);
    setSelectedStudents((current) => ids.every((id) => current.includes(id))
      ? current.filter((id) => !ids.includes(id))
      : [...new Set([...current, ...ids])]);
  };

  return (
    <WorkspacePage title="Academic periods" subtitle="Track yearly progression and short learning cohorts">
      <section className="management-page">
        {error && <div className="form-error">{error}</div>}
        <div className="people-hero">
          <div>
            <p className="eyebrow">Progression center</p>
            <h2>Keep every stage of learning connected.</h2>
            <p>Use academic years for Form 1–4 or Grade 1–6, and cohorts for short courses lasting weeks or months.</p>
          </div>
          <div className="people-hero-stat"><strong>{periods.length}</strong><span>periods and cohorts</span></div>
        </div>

        <form className="inline-form period-form" onSubmit={save}>
          <input placeholder="Name e.g. 2026–2027 or Web Design Cohort 1" required value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} />
          <select value={form.type} onChange={(event) => setForm({ ...form, type: event.target.value })}>
            <option value="academic_year">Academic year</option>
            <option value="cohort">Short-term cohort</option>
          </select>
          <input type="date" required value={form.startDate} onChange={(event) => setForm({ ...form, startDate: event.target.value })} />
          <input type="date" required value={form.endDate} onChange={(event) => setForm({ ...form, endDate: event.target.value })} />
          <input placeholder="Track e.g. Forms or Primary" value={form.track} onChange={(event) => setForm({ ...form, track: event.target.value })} />
          <button className="primary-small" type="submit">Create period</button>
        </form>

        <div className="table-card">
          <div className="table-heading"><div><p className="eyebrow">Configured periods</p><h3>Academic years and cohorts</h3></div></div>
          <div className="data-table">
            <div className="table-row table-head"><span>Name</span><span>Type</span><span>Dates</span><span>Status</span><span /></div>
            {periods.map((period) => (
              <div className="table-row" key={period._id}>
                <span><b>{period.name}</b><small>{period.track || 'School-wide'}</small></span>
                <span>{period.type === 'cohort' ? 'Cohort' : 'Academic year'}</span>
                <span>{new Date(period.startDate).toLocaleDateString()} – {new Date(period.endDate).toLocaleDateString()}</span>
                <span><em className="status-pill active">{period.status}</em></span>
                <button className="soft-button" type="button" onClick={() => initialize(period._id)}>Initialize students</button>
              </div>
            ))}
          </div>
          {!periods.length && <div className="empty-state"><strong>No academic periods yet</strong><span>Create an academic year or short-term cohort above.</span></div>}
        </div>

        <section className="enrollment-panel cohort-enrollment-panel">
          <div className="crud-form-heading"><div><p className="eyebrow">Short-term course enrollment</p><h3>Enroll selected students in a cohort</h3></div></div>
          <p className="cohort-enrollment-help">This adds students to this cohort only. Their current grade, section, and other period enrollments are left unchanged.</p>
          {cohortError && <div className="form-error" role="alert">{cohortError}</div>}
          {cohortMessage && <div className="form-success" role="status">{cohortMessage}</div>}
          <div className="form-grid">
            <label>Target cohort
              <select value={cohortPeriodId} onChange={(event) => { setCohortPeriodId(event.target.value); setCohortStudentIds([]); setCohortError(''); setCohortMessage(''); }}>
                <option value="">Choose a short-term cohort…</option>
                {eligibleCohorts.map((period) => <option key={period._id} value={period._id}>{period.name}</option>)}
              </select>
            </label>
            <label>Search active students
              <input placeholder="Name, email, admission number, grade, or section…" value={cohortSearch} onChange={(event) => setCohortSearch(event.target.value)} />
            </label>
          </div>
          <div className="progression-filters">
            <button type="button" className="soft-button" disabled={!visibleCohortStudents.length} onClick={toggleVisibleCohortStudents}>
              {visibleCohortStudents.length && visibleCohortStudents.every((student) => cohortStudentIds.includes(student._id)) ? 'Clear visible' : 'Select visible'} ({visibleCohortStudents.length})
            </button>
            <span>{cohortStudentIds.length} selected</span>
          </div>
          <div className="enrollment-list">
            {visibleCohortStudents.map((student) => (
              <label className="enrollment-row" key={student._id}>
                <input type="checkbox" checked={cohortStudentIds.includes(student._id)} onChange={() => setCohortStudentIds((current) => current.includes(student._id) ? current.filter((id) => id !== student._id) : [...current, student._id])} />
                <span><b>{student.user?.name || 'Student'}</b><small>{student.admissionNumber} · {student.grade}{student.section ? ` · ${student.section}` : ''}</small></span>
              </label>
            ))}
            {!visibleCohortStudents.length && <div className="empty-state"><strong>{activeStudents.length ? 'No matching active students' : 'No active students available'}</strong><span>Only active students from your school can be added.</span></div>}
          </div>
          <button type="button" className="primary-small" disabled={cohortBusy || !cohortPeriodId || !cohortStudentIds.length} onClick={enrollSelectedInCohort}>
            {cohortBusy ? 'Enrolling…' : `Enroll ${cohortStudentIds.length} selected student(s)`}
          </button>
        </section>

        <section className="enrollment-panel">
          <div className="crud-form-heading"><div><p className="eyebrow">Promotion and placement</p><h3>Move students into a new period</h3></div></div>
          <div className="form-grid">
            <label>Target period
              <select value={selected} onChange={(event) => setSelected(event.target.value)}>
                <option value="">Choose target period…</option>
                {periods.map((period) => <option key={period._id} value={period._id}>{period.name}</option>)}
              </select>
            </label>
            <label>New grade/form<input placeholder="Form 2 or Grade 3" value={grade} onChange={(event) => setGrade(event.target.value)} /></label>
          </div>
          <div className="progression-filters">
            <input className="enrollment-search" placeholder="Search by name, email, admission number, grade, or section…" value={studentSearch} onChange={(event) => setStudentSearch(event.target.value)} />
            <select className="filter-select" value={studentGradeFilter} onChange={(event) => setStudentGradeFilter(event.target.value)}>
              <option value="">All grades/forms</option>
              {grades.map((item) => <option key={item} value={item}>{item}</option>)}
            </select>
            <button type="button" className="soft-button" onClick={toggleVisible}>{visibleStudents.length && visibleStudents.every((student) => selectedStudents.includes(student._id)) ? 'Clear visible' : 'Select visible'} ({visibleStudents.length})</button>
          </div>
          <div className="enrollment-list">
            {visibleStudents.map((student) => (
              <label className="enrollment-row" key={student._id}>
                <input type="checkbox" checked={selectedStudents.includes(student._id)} onChange={() => setSelectedStudents((current) => current.includes(student._id) ? current.filter((id) => id !== student._id) : [...current, student._id])} />
                <span><b>{student.user?.name || 'Student'}</b><small>{student.admissionNumber} · Current: {student.grade}</small></span>
              </label>
            ))}
          </div>
          <button type="button" className="primary-small" onClick={promote}>Record progression for {selectedStudents.length} student(s)</button>
        </section>
      </section>
    </WorkspacePage>
  );
}
